import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useWebSocket, WebSocketMessage, TestRunStartedData, TestProgressData, TestRunCompletedData } from '../hooks/useWebSocket'

interface TestRunStatus {
  testSuiteId: number
  testSuiteName: string
  status: 'running' | 'completed' | 'failed'
  currentTest: number
  totalTests: number
  passed: number
  failed: number
  startedAt?: string
  duration?: number
}

interface WebSocketContextValue {
  isConnected: boolean
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error'
  lastMessage: WebSocketMessage | null
  activeTestRuns: Map<number, TestRunStatus>
  notifications: Notification[]
  clearNotifications: () => void
  dismissNotification: (id: string) => void
}

interface Notification {
  id: string
  type: 'success' | 'error' | 'info' | 'warning'
  title: string
  message: string
  timestamp: string
  testSuiteId?: number
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider')
  }
  return context
}

interface WebSocketProviderProps {
  children: ReactNode
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [activeTestRuns, setActiveTestRuns] = useState<Map<number, TestRunStatus>>(new Map())
  const [notifications, setNotifications] = useState<Notification[]>([])

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    }
    setNotifications(prev => [newNotification, ...prev].slice(0, 10)) // Keep last 10
  }, [])

  const handleTestRunStarted = useCallback((data: TestRunStartedData) => {
    setActiveTestRuns(prev => {
      const updated = new Map(prev)
      updated.set(data.testSuiteId, {
        testSuiteId: data.testSuiteId,
        testSuiteName: data.testSuiteName,
        status: 'running',
        currentTest: 0,
        totalTests: data.totalTests,
        passed: 0,
        failed: 0,
        startedAt: data.startedAt,
      })
      return updated
    })

    addNotification({
      type: 'info',
      title: 'Test Run Started',
      message: `"${data.testSuiteName}" is now running (${data.totalTests} tests)`,
      testSuiteId: data.testSuiteId,
    })
  }, [addNotification])

  const handleTestProgress = useCallback((data: TestProgressData) => {
    setActiveTestRuns(prev => {
      const updated = new Map(prev)
      const existing = updated.get(data.testSuiteId)
      if (existing) {
        updated.set(data.testSuiteId, {
          ...existing,
          currentTest: data.currentTest,
          passed: data.passed,
          failed: data.failed,
        })
      }
      return updated
    })
  }, [])

  const handleTestRunCompleted = useCallback((data: TestRunCompletedData) => {
    setActiveTestRuns(prev => {
      const updated = new Map(prev)
      updated.delete(data.testSuiteId) // Remove from active runs
      return updated
    })

    const isSuccess = data.status === 'completed' && data.failed === 0
    addNotification({
      type: isSuccess ? 'success' : 'warning',
      title: isSuccess ? 'Test Run Passed' : 'Test Run Completed',
      message: `"${data.testSuiteName}": ${data.passed}/${data.totalTests} passed${data.failed > 0 ? `, ${data.failed} failed` : ''}`,
      testSuiteId: data.testSuiteId,
    })
  }, [addNotification])

  const { isConnected, connectionState, lastMessage } = useWebSocket({
    onTestRunStarted: handleTestRunStarted,
    onTestProgress: handleTestProgress,
    onTestRunCompleted: handleTestRunCompleted,
  })

  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const value: WebSocketContextValue = {
    isConnected,
    connectionState,
    lastMessage,
    activeTestRuns,
    notifications,
    clearNotifications,
    dismissNotification,
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export default WebSocketProvider
